import { useEffect, useMemo, useState } from 'react';
import { Title, useCreatePath, useNotify, useRedirect } from 'react-admin';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useCompanies } from '../shared/companies';
import { FeaturesLoading, useFeatureState } from '../admin/featureFlags/FeatureGate';
import { useHasPermission } from '../admin/PermissionGate';
import { toErrorMessage } from './preOrderApi';
import { ContractorPicker } from './ContractorPicker';
import type { ContractorRef } from './preOrderContractorApi';
import {
  CONTRACTOR_FIELD,
  COPY_DECISION_LABELS,
  DATE_FIELDS,
  FEATURE_PRE_ORDER_COPY,
  NUMBER_FIELDS,
  createPreOrderCopy,
  decisionsBody,
  fetchCopyComparison,
  initialChoices,
  missingDecisions,
  sameDatesAsSource,
  sourceText,
  type CopyComparison,
  type CopyDecision,
  type CopyRow,
  type FieldChoice,
} from './preOrderCopyApi';

/**
 * Kopia prezlecenia z porównaniem pole po polu (makieta back office v2,
 * BR-ADM-09, ścieżka ADM-03; widok Administracji „Kopia z porównaniem”;
 * 20.10.2026, przełącznik `preOrderCopy`).
 *
 * Przy każdym polu: wartość źródła, decyzja — zostaw, zmień, wyczyść —
 * i wartość kopii. Daty i numer zamówienia klienta są „do potwierdzenia”:
 * bez propozycji, operator wybiera sam; numer nie przechodzi na kopię.
 * „Utwórz kopię” działa dopiero z decyzją przy każdym polu — zapis obok
 * porównania nie istnieje (API też odmawia). Kopia jest zwykłym
 * prezleceniem do akceptacji.
 *
 * Odstępstwo od makiety: bez kolumny „bieżące źródło (DOCX, mail)” —
 * ekstrakcja z maili nie jest po naszej stronie; wartość z bieżącego
 * zlecenia klienta wpisuje operator przy „zmień”.
 */

export const FEATURE_OFF_NOTE = 'Ta funkcja jest wyłączona w wersji aplikacji, z której korzystasz.';
export const NO_PERMISSION_NOTE =
  'Kopię prezlecenia tworzy przyjęcie zleceń (CS i Administracja) — Twoje konto nie ma tego uprawnienia.';

export const PreOrderCopy = () => {
  const on = useFeatureState(FEATURE_PRE_ORDER_COPY);
  const allowed = useHasPermission('ORDER_INTAKE');
  const { id } = useParams<{ id: string }>();
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !allowed || !id) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title="Kopia prezlecenia" />
        <Alert severity="info">{on ? NO_PERMISSION_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <CopyScreen id={id} />;
};

/** Pole nowej wartości przy „zmień” — rodzaj z pola nagłówka. */
const ValueInput = ({
  row,
  value,
  onChange,
  contractor = null,
  onContractor,
  clientName = null,
  disabled,
}: {
  row: CopyRow;
  value: string;
  onChange: (v: string) => void;
  /** Wiersz kontrahenta (21.10.2026): wybór z kartoteki zamiast pola tekstu. */
  contractor?: ContractorRef | null;
  onContractor?: (c: ContractorRef | null) => void;
  clientName?: string | null;
  disabled: boolean;
}) => {
  const companies = useCompanies();
  if (row.field === CONTRACTOR_FIELD && onContractor) {
    return (
      <ContractorPicker value={contractor} onChange={onContractor} suggestFrom={clientName} disabled={disabled} />
    );
  }
  if (row.field === 'companyId') {
    return (
      <TextField
        select
        size="small"
        fullWidth
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        slotProps={{ htmlInput: { 'aria-label': `${row.label} — nowa wartość` } }}
      >
        {companies.map((c) => (
          <MenuItem key={c.id} value={String(c.id)}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      type={DATE_FIELDS.includes(row.field) ? 'datetime-local' : NUMBER_FIELDS.includes(row.field) ? 'number' : 'text'}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{
        htmlInput: { 'aria-label': `${row.label} — nowa wartość`, 'data-testid': `copy-value-${row.field}` },
      }}
    />
  );
};

/**
 * Nazwa klienta kopii — od niej startuje wyszukiwarka kontrahenta: nowa
 * wartość przy „zmień”, inaczej wartość źródła.
 */
function clientNameOf(rows: ReadonlyArray<CopyRow>, choices: Record<string, FieldChoice>): string | null {
  const row = rows.find((r) => r.field === 'clientName');
  const choice = choices.clientName;
  if (choice?.decision === 'change' && choice.value.trim() !== '') return choice.value;
  return typeof row?.source === 'string' ? row.source : null;
}

const CopyScreen = ({ id }: { id: string }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const createPath = useCreatePath();
  const companies = useCompanies();
  const [comparison, setComparison] = useState<CopyComparison | null>(null);
  const [choices, setChoices] = useState<Record<string, FieldChoice>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    fetchCopyComparison(id)
      .then((c) => {
        if (!active) return;
        setComparison(c);
        setChoices(initialChoices(c.rows));
      })
      .catch((e: unknown) => {
        if (active) setError(toErrorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [id]);

  const missing = useMemo(
    () => (comparison ? missingDecisions(comparison.rows, choices) : []),
    [comparison, choices],
  );

  const set = (field: string, patch: Partial<FieldChoice>) =>
    setChoices((c) => ({ ...c, [field]: { ...(c[field] ?? { decision: null, value: '' }), ...patch } }));

  const save = () =>
    void (async () => {
      if (!comparison) return;
      setBusy(true);
      try {
        const copy = await createPreOrderCopy(id, decisionsBody(comparison.rows, choices));
        notify(`Powstała kopia — prezlecenie #${copy.id}`, { type: 'success' });
        redirect('show', 'pre-orders', copy.id);
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  const sourceLink = (
    <Link to={createPath({ resource: 'pre-orders', type: 'show', id })}>
      prezlecenie #{id}
      {comparison?.source.orderNumber ? ` (zlecenie ${comparison.source.orderNumber})` : ''}
    </Link>
  );

  return (
    <Box sx={{ p: 2 }} data-testid="pre-order-copy">
      <Title title="Kopia prezlecenia" />
      {/* Kontrakt `data-testid` paczki (S-22.10-02): źródło kopii — u nas zawsze bieżące prezlecenie. */}
      <Typography variant="h6" component="h1" sx={{ mb: 1 }} data-testid="adm-kopia-nowa-zrodlo">
        Kopia z porównaniem — źródło: {sourceLink}
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Pola skopiowane z poprzedniego zlecenia nie są potwierdzone bez porównania z bieżącym zleceniem klienta.
        Przy każdym polu zdecyduj: zostaw, zmień albo wyczyść. Daty i numer zamówienia klienta wymagają Twojej
        decyzji; numer nie przechodzi na kopię.
      </Alert>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {comparison === null && !error ? <CircularProgress size={20} /> : null}
      {comparison ? (
        <>
          <Table size="small" sx={{ '& td, & th': { px: 0.75, verticalAlign: 'top' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Pole</TableCell>
                <TableCell>Prezlecenie #{comparison.source.id}</TableCell>
                <TableCell>Decyzja</TableCell>
                <TableCell sx={{ minWidth: 260 }}>Kopia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparison.rows.map((row) => {
                const choice = choices[row.field] ?? { decision: null, value: '' };
                return (
                  <TableRow
                    key={row.field}
                    data-testid={`copy-row-${row.field}`}
                    sx={row.needsConfirmation && choice.decision === null ? { backgroundColor: 'rgba(237, 108, 2, 0.08)' } : {}}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.label}
                      </Typography>
                      {row.needsConfirmation ? (
                        <Chip size="small" color="warning" variant="outlined" label="do potwierdzenia" sx={{ mt: 0.5 }} />
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{sourceText(row, companies)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={choice.decision}
                        disabled={busy}
                        onChange={(_e, v: CopyDecision | null) => {
                          if (v) set(row.field, { decision: v });
                        }}
                        aria-label={`${row.label} — decyzja`}
                      >
                        {row.decisions.map((d) => (
                          <ToggleButton
                            key={d}
                            value={d}
                            data-testid={`copy-decision-${row.field}-${d}`}
                            sx={{ py: 0.25, px: 1, textTransform: 'none' }}
                          >
                            {COPY_DECISION_LABELS[d]}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell>
                      {choice.decision === 'change' ? (
                        <ValueInput
                          row={row}
                          value={choice.value}
                          disabled={busy}
                          onChange={(v) => set(row.field, { value: v })}
                          contractor={choice.contractor ?? null}
                          onContractor={(c) => set(row.field, { contractor: c, value: c ? String(c.id) : '' })}
                          clientName={clientNameOf(comparison.rows, choices)}
                        />
                      ) : choice.decision === 'keep' ? (
                        <Typography variant="body2">{sourceText(row, companies)}</Typography>
                      ) : choice.decision === 'clear' ? (
                        <Typography variant="body2" color="text.secondary">
                          — (puste)
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="warning.main">
                          wybierz decyzję
                        </Typography>
                      )}
                      {row.hint ? (
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                          {row.hint}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {sameDatesAsSource(choices) ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Daty zostają takie jak w źródle — przed zapisem do VIA kontrola duplikatów pokaże źródło jako możliwy
              duplikat.
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              disabled={busy || missing.length > 0}
              onClick={save}
              // Kontrakt `data-testid` paczki (S-22.10-02): zgodność potwierdza się polem po polu, przycisk tworzy kopię.
              data-testid="adm-kopia-porownanie-zgodne"
            >
              Utwórz kopię
            </Button>
            <Button component={Link} to={createPath({ resource: 'pre-orders', type: 'show', id })} disabled={busy}>
              Anuluj
            </Button>
            {busy ? <CircularProgress size={18} /> : null}
            {missing.length > 0 ? (
              <Typography variant="body2" color="warning.main" data-testid="copy-missing">
                Brak decyzji: {missing.join(', ')}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Kopia powstanie jako nowe prezlecenie do akceptacji; w historii obu zostanie ślad kopii.
              </Typography>
            )}
          </Stack>
        </>
      ) : null}
    </Box>
  );
};
